import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { agenciesApi } from '../../lib/api';
import { PropertyCard } from '../../components/property/PropertyCard';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Building, MapPin, Phone, Mail, Globe, Home, ArrowLeft } from 'lucide-react';

export const AgencyDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const [agency, setAgency] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const seoTitle = agency ? `${agency.agency_name} - Agence immobiliere | Beytam` : 'Beytam';

  useSEO({
    title: seoTitle,
    description: agency?.description || seoTitle,
    ogTitle: seoTitle,
    canonical: window.location.href,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [agencyRes, propsRes] = await Promise.all([
          agenciesApi.get(id),
          agenciesApi.getProperties(id),
        ]);
        setAgency(agencyRes.data);
        setProperties(propsRes.data?.properties || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Skeleton className="h-48 w-full rounded-xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-80 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">{language === 'fr' ? 'Agence non trouvee' : 'الوكالة غير موجودة'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-detail-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/agencies" className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-6 text-sm" data-testid="back-to-agencies">
            <ArrowLeft className="w-4 h-4" /> {language === 'fr' ? 'Toutes les agences' : 'كل الوكالات'}
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
              {agency.logo ? (
                <img src={agency.logo} alt={agency.agency_name} className="w-20 h-20 rounded-2xl object-cover" />
              ) : (
                <Building className="w-10 h-10 text-white" />
              )}
            </div>
            <div>
              <h1 className="font-manrope text-2xl md:text-3xl font-bold mb-2">{agency.agency_name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-blue-200">
                {agency.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {agency.city}</span>}
                {agency.phone && <a href={`tel:${agency.phone}`} className="flex items-center gap-1 hover:text-white"><Phone className="w-4 h-4" /> {agency.phone}</a>}
                {agency.email && <a href={`mailto:${agency.email}`} className="flex items-center gap-1 hover:text-white"><Mail className="w-4 h-4" /> {agency.email}</a>}
                {agency.website && <a href={agency.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white"><Globe className="w-4 h-4" /> {language === 'fr' ? 'Site web' : 'الموقع'}</a>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Description */}
        {agency.description && (
          <Card className="border-0 shadow-md mb-8">
            <CardContent className="p-6">
              <h2 className="font-semibold text-slate-900 mb-3">
                {language === 'fr' ? 'A propos' : 'عن الوكالة'}
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {language === 'ar' && agency.description_ar ? agency.description_ar : agency.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Address */}
        {agency.address && (
          <Card className="border-0 shadow-md mb-8">
            <CardContent className="p-6">
              <h2 className="font-semibold text-slate-900 mb-2">
                {language === 'fr' ? 'Adresse' : 'العنوان'}
              </h2>
              <p className="text-slate-600 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" /> {agency.address}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Properties */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-manrope text-xl font-bold text-slate-900">
            {language === 'fr' ? `Annonces de l'agence` : 'إعلانات الوكالة'}
            <Badge className="ml-3 bg-blue-50 text-blue-800 border-0">{agency.listing_count || 0}</Badge>
          </h2>
        </div>

        {properties.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-12 text-center">
              <Home className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">
                {language === 'fr' ? 'Aucune annonce publiee par cette agence' : 'لا توجد إعلانات منشورة من هذه الوكالة'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};
